import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  X, Download, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCw,
  FileText, User, Clock, Trash2
} from 'lucide-react';
import { formatIST } from '@/lib/utils';

export default function AttachmentViewer({
  attachment,
  attachments = [],
  onClose,
  onDelete,
  canDelete = false,
  categories = [],
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  // Find current attachment in list
  useEffect(() => {
    if (attachment && attachments.length > 0) {
      const idx = attachments.findIndex((a) => a.id === attachment.id);
      if (idx >= 0) setCurrentIndex(idx);
    }
  }, [attachment, attachments]);

  const current = attachments[currentIndex] || attachment;
  const category = categories.find((c) => c.id === current?.category_id);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : attachments.length - 1));
    setZoom(1);
    setRotation(0);
  }, [attachments.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < attachments.length - 1 ? prev + 1 : 0));
    setZoom(1);
    setRotation(0);
  }, [attachments.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') handlePrev();
      else if (e.key === 'ArrowRight') handleNext();
      else if (e.key === 'Escape') onClose?.();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrev, handleNext, onClose]);

  if (!current) return null;

  const isImage = current.file_kind === 'image';
  const isPdf = current.file_kind === 'pdf';

  return (
    <Dialog open={!!current} onOpenChange={() => onClose?.()}>
      <DialogContent className="max-w-4xl w-[95vw] h-[90vh] p-0 bg-slate-900 border-slate-800">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/70 to-transparent p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {category && (
                <Badge
                  style={{
                    backgroundColor: `${category.color_hex}30`,
                    color: category.color_hex,
                  }}
                >
                  {category.name}
                </Badge>
              )}
              <span className="text-white text-sm font-medium">
                {current.original_filename}
              </span>
              {attachments.length > 1 && (
                <span className="text-white/60 text-sm">
                  {currentIndex + 1} / {attachments.length}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isImage && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/20"
                    onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="text-white/60 text-xs w-12 text-center">
                    {Math.round(zoom * 100)}%
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/20"
                    onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/20"
                    onClick={() => setRotation((r) => r + 90)}
                  >
                    <RotateCw className="h-4 w-4" />
                  </Button>
                </>
              )}
              {current.download_url && (
                <a href={current.download_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/20">
                    <Download className="h-4 w-4" />
                  </Button>
                </a>
              )}
              {canDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-400 hover:bg-red-500/20"
                  onClick={() => onDelete?.(current)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
                onClick={() => onClose?.()}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center overflow-hidden p-12">
          {isImage && current.download_url ? (
            <img
              src={current.download_url}
              alt={current.original_filename}
              className="max-w-full max-h-full object-contain transition-transform"
              style={{
                transform: `scale(${zoom}) rotate(${rotation}deg)`,
              }}
            />
          ) : isPdf && current.download_url ? (
            <iframe
              src={current.download_url}
              title={current.original_filename}
              className="w-full h-full bg-white rounded"
            />
          ) : (
            <div className="text-center text-white">
              <FileText className="h-24 w-24 mx-auto text-slate-500 mb-4" />
              <p className="text-lg">{current.original_filename}</p>
              <p className="text-slate-400 text-sm mt-2">Preview not available</p>
            </div>
          )}
        </div>

        {/* Navigation Arrows */}
        {attachments.length > 1 && (
          <>
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              onClick={handlePrev}
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              onClick={handleNext}
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
          <div className="flex items-center justify-between text-white/80 text-sm">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <User className="h-4 w-4" />
                {current.uploaded_by_name || current.uploaded_by_role}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {formatIST(current.uploaded_at_ist)}
              </span>
            </div>
            {current.caption && (
              <p className="text-white/60 max-w-md truncate">{current.caption}</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
